import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import useStores from '@stores/useStores';
import { RedirectRoute } from '@components/routes/redirect';
import { entitled } from '@components/routes/routeEntitlement';
import type { UseIsRouteEnabled } from '@components/routes/routeEntitlement';
import { useHasNewPermissionsModel } from '@components/routes/entitlements';
import { SECTION } from '@hooks/useDocumentTitle/constants';

import { ACCESS_MANAGEMENT_PATH } from './paths';

const ACCESS_DENIED_MESSAGE =
  "This feature isn't available on your current plan or the route is invalid. Redirecting to Access Management.";

/**
 * Only for gates on pages INSIDE the section. The section root must not use it:
 * its own path is the redirect target, so an unentitled visitor would be sent to
 * the URL they are already on and left on a blank pane.
 */
const ACCESS_MANAGEMENT_REDIRECT = {
  to: ACCESS_MANAGEMENT_PATH.ACCESS_MANAGEMENT,
  message: ACCESS_DENIED_MESSAGE,
};

/**
 * Kept local rather than in @components/routes/entitlements: these gate pages
 * inside this section only.
 */
const useHasDefaultPermissions: UseIsRouteEnabled = () =>
  useStores().featuresStore.has('DEFAULT_PERMISSIONS');

const useHasGroups: UseIsRouteEnabled = () => useStores().featuresStore.has('GROUPS');

const useHasServiceAccessTokens: UseIsRouteEnabled = () =>
  useStores().featuresStore.has('SERVICE_ACCESS_TOKENS');

const AccessManagementLayout = lazy(() =>
  import(/* webpackChunkName: "accessManagement" */ './routesLayout').then((module) => ({
    default: module.AccessManagementLayout,
  })),
);

const AccessManagement = lazy(() =>
  import(/* webpackChunkName: "accessManagement" */ '@components/accessManagement').then(
    (module) => ({
      default: module.AccessManagement,
    }),
  ),
);

const DefaultWorkspacePolicyPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.policy" */ '../components/defaultWorkspacePolicyPage'
  ).then((module) => ({
    default: module.DefaultWorkspacePolicyPage,
  })),
);

const GroupDetailsPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.groupDetails" */ '../components/groupDetailsPage'
  ).then((module) => ({
    default: module.GroupDetailsPage,
  })),
);

const GroupWorkspacePolicyPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.groupWorkspacePolicy" */ '../components/groupWorkspacePolicyPage'
  ).then((module) => ({
    default: module.GroupWorkspacePolicyPage,
  })),
);

const MemberDetailsPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.memberDetails" */ '../components/memberDetailsPage'
  ).then((module) => ({
    default: module.MemberDetailsPage,
  })),
);

const MemberWorkspacePolicyPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.memberWorkspacePolicy" */ '../components/memberWorkspacePolicyPage'
  ).then((module) => ({
    default: module.MemberWorkspacePolicyPage,
  })),
);

const WorkspaceTokenAccessPolicyPage = lazy(() =>
  import(
    /* webpackChunkName: "accessManagement.workspaceTokenAccessPolicy" */ '../components/serviceAccessTokens/workspaceTokenAccessPolicyPage'
  ).then((module) => ({
    default: module.WorkspaceTokenAccessPolicyPage,
  })),
);

/**
 * See the note in @components/botManagement/routes on why these are definitions.
 *
 * Only the seven path constants in ./paths that describe a URL path appear here.
 * The rest — GROUPS, MEMBERS, INVITATIONS, SCIM_GROUP_MAPPING and the service
 * access token list — are `?tab=` query strings onto the index route, not routes,
 * and must not become route definitions.
 */
export const accessManagementRoutes = (
  <Route
    key={ACCESS_MANAGEMENT_PATH.ACCESS_MANAGEMENT}
    path={ACCESS_MANAGEMENT_PATH.ACCESS_MANAGEMENT}
    element={entitled(useHasNewPermissionsModel, <AccessManagementLayout />)}
    handle={{ section: SECTION.accessManagement }}
  >
    <Route index element={<AccessManagement />} />
    <Route
      path="policy/:workspaceId"
      element={entitled(
        useHasDefaultPermissions,
        <DefaultWorkspacePolicyPage />,
        ACCESS_MANAGEMENT_REDIRECT,
      )}
    />
    {/* `subsection` classifies the entity instead of describing an action on it,
        so it sits after the name: `Engineering - Groups - Access Management`.
        These sub-sections are `?tab=` views of the index route, never routes. */}
    <Route
      path="groups/:groupId"
      element={entitled(useHasGroups, <GroupDetailsPage />, ACCESS_MANAGEMENT_REDIRECT)}
      handle={{ subsection: SECTION.groups }}
    />
    <Route
      path="groups/:groupId/policy/:workspaceId"
      element={entitled(useHasGroups, <GroupWorkspacePolicyPage />, ACCESS_MANAGEMENT_REDIRECT)}
      handle={{ subsection: SECTION.groups }}
    />
    <Route
      path="members/:memberId"
      element={<MemberDetailsPage />}
      handle={{ subsection: SECTION.members }}
    />
    <Route
      path="members/:userId/policy/:workspaceId"
      element={<MemberWorkspacePolicyPage />}
      handle={{ subsection: SECTION.members }}
    />
    <Route
      path="serviceAccessTokens/:tokenId/policy/:workspaceId"
      element={entitled(
        useHasServiceAccessTokens,
        <WorkspaceTokenAccessPolicyPage />,
        ACCESS_MANAGEMENT_REDIRECT,
      )}
      handle={{ subsection: SECTION.serviceAccessTokens }}
    />
    <Route
      path="*"
      element={
        <RedirectRoute
          to={ACCESS_MANAGEMENT_PATH.ACCESS_MANAGEMENT}
          message={ACCESS_DENIED_MESSAGE}
        />
      }
    />
  </Route>
);
